import subscription from './subscription.png'
import publish from './publish.png'
import noEvent from './no-event.png'
import eventNoContext from './event-no-context.png'
import eventImage from './event.png'

const content = `<p>Events are an application of the Observer pattern, at larger scale and with less coupling than the Observer pattern.</p>

<p>In the traditional Observer pattern, the subscribers know about the caller and subscribe to it. The caller then loops over its subscribers to calls them.</p>

<p>Events are a variation of this pattern, where the callees don’t subscribe to the caller directly. Instead, there is a central notification system where subscribers declare an interest for a type of event.</p>

<p>Callers publish events to the notification system, and the listeners that are interested in those events receive them.</p>

<p>Callers don’t know their list of receivers, and receivers don’t know their callers. It’s the notification system that calls the subscribers (also called “listeners”) that subscribed to a given event when it gets published by a caller (also called “publisher”).</p>

<p>More precisely, the subscribers first subscribe to the event they need to react to:</p>

<img src=${subscription} alt="subscription" />

<p>Then whenever a publisher publishes an event, the corresponding listeners receive it:</p>

<img src=${publish} alt="publish" />

<p>To illustrate, consider an application where a user can access different kinds of media, books, images and videos, that are handled by distinct parts of the code.</p>

<p>When the user deletes their account, we need to remove their accesses to all the kinds of media. One way to process without using events is this:</p>

<pre><code class="language-ruby">class Account
  def delete!
    Books::remove_accesses(account.id)
    Images::handle_account_deletion(account.id)
    Videos::unsubscribe(account.id)
  end
end</code></pre>

<p>But instead of having <code>Account</code> know about the various parts of the system, we can make it publish an event that says to the world: “to whomever may be interested, an account is being deleted!”:</p>

<pre><code class="language-ruby">class Account
  def delete!
    Events.publish(AccountDeletedEvent.new(account_id: id))
  end
end</code></pre>

<p>(Various possible syntaxes would make sense to send events. The above example assumes the presence of a service called <code>Events</code> that handles publications and subscriptions).</p>

<p>On the other side, the individual modules that need to react to this event subscribe to it. For example, on books side:</p>

<pre><code class="language-ruby">module Books
  Events.listen_to AccountDeletedEvent do |event|
    remove_accesses(event.account_id)
  end
end</code></pre>

<p>This way, the <code>Account</code> no longer knows about the other parts of the system. It just publishes an event describing what happened on its side. On the media side, the code listens to that event and reacts accordingly.</p>

<h2 id="The-effect-on-coupling">The effect on coupling</h2>

<p>Let’s examine the effect that introducing events had on coupling in the code.</p>

<p>Before introducing events, the code of <code>Account</code> knew about the code of <code>Books</code> (among others things):</p>

<img src=${noEvent} alt="on-event" />

<p>By introducing the event, <code>Account</code> no longer knows about <code>Books</code>. Instead <code>Account</code> knows about <code>AccountDeletedEvent</code> and <code>Books</code> also knows about <code>AccountDeletedEvent</code>:</p>

<img src=${eventNoContext} alt="event-no-context" />

<p>But if we take a step back, <code>AccountDeletedEvent</code> is related to the lifecycle of <code>Account</code> so it belongs to the context of <code>Account</code> more than the context of media:</p>

<img src=${eventImage} alt="event" />

<p>With this event we both inverted and reduced the dependency between <code>Account</code> and <code>Books</code>:</p>

<ul><li>we inverted the dependency: now it’s <code>Books</code> that knows about the <code>Account</code> context</li><li>we reduced the dependency: <code>Book</code> doesn’t know about <code>Account</code>, it just knows about the smaller <code>AccountDeletedEvent</code></li></ul>

<p>Adding events made us add more code. Is this an improvement over the original design without events?</p>

<p>In our case, making <code>Books</code> depend on account concepts rather than the other way round is arguably an improvement because:</p>

<ul><li>accounts should be able to exist without books, so their code should be independent of books</li><li>we can expect many parts of an application to depend on accounts. If <code>Account</code> knew about <code>Books</code> along with all the others, it would have low <a href="/high-cohesion">cohesion</a>.</li></ul>

<p>But if it weren’t for those two reasons, events would not have been worth their overhead in our case. To illustrate this, let’s consider the opposite situation: if <code>Books</code> needs to call <code>Account</code>.</p>

<p>In that case, using events would be a poor design choice. Indeed it would make sense for <code>Books</code> to call <code>Account</code> directly rather than making <code>Account</code> listen to an event published by <code>Books</code> because:</p>

<ul><li>books use accounts, so it makes sense that the code of <code>Books</code> shows some mentions of <code>Account</code></li><li>making <code>Account</code> listen to events coming from <code>Books</code> and all the contexts that depend on it would lower its cohesion</li><li>since <code>Books</code> already depend on the account context by listening to <code>AccountDeletedEvent</code>, making <code>Account</code> listen to an event published by <code>Books</code> would create a circular dependency, which creates strong coupling by preventing both contexts to be considered in isolation</li></ul>

<h2 id="Decoupling-from-the-notification-system-itself">Decoupling from the notification system itself</h2>

<p>With this design, the publishers and listeners are decoupled from one another, but they can end up being coupled with the implementation choice of the notification system itself.</p>

<p>This is a problem because there are many notifications systems out there, and if publishers and listeners are coupled to one it will make it difficult to switch to another system. Also, the particular system used is an implementation detail from the perspective of the rest of the codebase.</p>

<p>One way to avoid this problem is to wrap the API of the notification system inside of a set of classes and functions that are defined in your codebase, and that offers to the rest of your code an interface that doesn’t show the API notification system.</p>

<p>In the above code, <code>Events</code> is such a wrappers.</p>

<h2 id="Examples-of-notification-systems">Examples of notification systems</h2>

<p>Libraries for handling events are available in many languages. In Ruby only there is Rails' native <a href="https://api.rubyonrails.org/classes/ActiveSupport/Notifications.html">ActiveSupport::Notifications</a> along with many Ruby gems such as <a href="https://github.com/krisleech/wisper">wisper</a>, <a href="https://github.com/RailsEventStore/rails_event_store">Rails event store</a> and <a href="https://github.com/dry-rb/dry-events">dry-events</a>.</p>

<p>Another kind of notification libraries are called “message brokers”. They follow the same principles as above but act at a higher-level, as they can pass events across processes and programs. They are therefore independent of the programming languages used by the programs. Popular examples of such libraries include <a href="https://kafka.apache.org/">Kafka</a>, <a href="https://www.rabbitmq.com/">RabbitMQ</a> and <a href="https://activemq.apache.org/">ActiveMQ</a>.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Events decouple publishers and listeners from one another</li><li>Listeners subscribe to events to receive them when publishers emit them</li><li>Avoid coupling with the notification system itself</li><li>There are many libraries to handle notifications, at various scales</li></ul>
</div>`

export default content
